import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Input,
  useTableFeatures,
  useTableSort,
  createTableColumn,
  Badge,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";
import { DeleteRegular, Add20Filled, EditRegular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import "../CSS/AgentAIComponent.css";
import { getAccessToken } from "../../Services/GraphServices";
import { DeleteAgentAI, GetAllAgentAI } from "../../Services/HeedifyServices";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { useNavigate } from "react-router-dom";
import { useAbility } from "../../Contexts/AbilityContext";

// Normalise la réponse de GetAllAgentAI (array | objet map | {agentAIs/data:[...]})
const normalizeAgentAIs = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.agentAIs)) return res.agentAIs;
  if (Array.isArray(res.data)) return res.data;
  if (typeof res === "object") {
    return Object.values(res).filter(
      (v) => v && typeof v === "object" && (v.id || v.displayName)
    );
  }
  return [];
};

// Mappe le doc Cosmos iaAgent vers les colonnes affichées.
const mapAgentAIRow = (a) => ({
  ...a,
  _name: a.displayName || a.agentPersonality?.name || a.id || "",
  _entity: a.scope || "",
  _voice: a.agentPersonality?.voice || "",
});

const AgentAIListComponent = (props) => {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ability = useAbility();

  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedAgentAI, setSelectedAgentAI] = useState(null);
  const [filterVisible, setFilterVisible] = useState({
    NAME: false,
    ENTITY: false,
    VOICE: false,
  });
  const [filters, setFilters] = useState({
    NAME: "",
    ENTITY: "",
    VOICE: "",
  });

  const { ACTIVE_TENANT, SCOPES_NAMES, st_AgentAIs, setAgentAIs } = props;
  // Clé stable des scopes (évite une boucle de re-fetch si la ref du tableau change).
  const scopesKey = Array.isArray(SCOPES_NAMES) ? SCOPES_NAMES.join(",") : "";

  // --- Chargement réel : GetAllAgentAI par scope, agrégé puis dispatché ---
  useEffect(() => {
    const scopes = scopesKey ? scopesKey.split(",") : [];
    if (!ACTIVE_TENANT || scopes.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken(instance, activeAccount);
        const perScope = await Promise.all(
          scopes.map((scope) =>
            GetAllAgentAI(ACTIVE_TENANT, userId, scope, token).then(normalizeAgentAIs)
          )
        );
        if (cancelled) return;
        const byId = new Map();
        perScope.flat().forEach((a) => {
          if (a && a.id) byId.set(a.id, a);
        });
        const all = Array.from(byId.values());
        console.log(`🤖 Agent AIs loaded: ${all.length}`, all);
        setAgentAIs(all);
      } catch (e) {
        console.error("❌ Error loading Agent AIs:", e);
        if (!cancelled) setAgentAIs([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ACTIVE_TENANT, scopesKey]);

  // --- Lignes (depuis le store) + permissions par scope ---
  const agentAIRows = useMemo(() => {
    const list = Array.isArray(st_AgentAIs) ? st_AgentAIs : [];
    return list.map((a) => {
      const row = mapAgentAIRow(a);
      const scope = row._entity;
      return {
        ...row,
        permissions: {
          main: {
            canCreate: ability?.can("create", `Flow.main@${scope}`),
            canRead: ability?.can("read", `Flow.main@${scope}`),
            canUpdate: ability?.can("update", `Flow.main@${scope}`),
            canDelete: ability?.can("delete", `Flow.main@${scope}`),
          },
        },
      };
    });
  }, [st_AgentAIs, ability]);

  // --- Filtrage (texte uniquement). On n'écarte PAS par canRead : le backend
  // renvoie déjà les agents du tenant/scope ; le gating se fait sur edit/delete. ---
  const applyFilters = useCallback((rows, f) => {
    const inc = (val, q) => (val || "").toLowerCase().includes((q || "").toLowerCase());
    return rows.filter(
      (a) =>
        inc(a._name, f.NAME) &&
        inc(a._entity, f.ENTITY) &&
        inc(a._voice, f.VOICE)
    );
  }, []);

  useEffect(() => {
    setFilteredRows(applyFilters(agentAIRows, filters));
  }, [filters, agentAIRows, applyFilters]);

  // --- Tri ---
  const columns = [
    createTableColumn({ columnId: "NAME", compare: (a, b) => a._name.localeCompare(b._name) }),
    createTableColumn({ columnId: "ENTITY", compare: (a, b) => a._entity.localeCompare(b._entity) }),
    createTableColumn({ columnId: "VOICE", compare: (a, b) => a._voice.localeCompare(b._voice) }),
    createTableColumn({ columnId: "ACTION" }),
  ];

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures(
    { columns, items: filteredRows },
    [useTableSort({ defaultSortState: { sortColumn: "NAME", sortDirection: "ascending" } })]
  );

  const rows = useMemo(() => sort(getRows()), [sort, getRows]);

  const headerSortProps = (columnId) => ({
    onClick: (e) => toggleColumnSort(e, columnId),
    sortDirection: getSortDirection(columnId),
  });

  const handleFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const toggleFilterVisibility = (col) => setFilterVisible((prev) => ({ ...prev, [col]: !prev[col] }));

  // --- Suppression ---
  const removeAgentAI = (agentAIId, scope) => {
    getAccessToken(instance, activeAccount).then((token) => {
      DeleteAgentAI(ACTIVE_TENANT, userId, agentAIId, scope, token)
        .then((response) => {
          if (response && (response.success === true || response.message === "deleted")) {
            setAgentAIs((Array.isArray(st_AgentAIs) ? st_AgentAIs : []).filter((ai) => ai.id !== agentAIId));
          } else if (response) {
            console.error("❌ Failed to delete Agent AI:", response.message);
          }
        })
        .catch((error) => console.error("❌ Error deleting Agent AI:", error))
        .finally(() => setSelectedAgentAI(null));
    });
  };

  const addAgentAI = () => navigate("/iaagent/new");
  const selectAgentAI = (id) => navigate(`/iaagent/${id}`);

  const hasAnyCreatePermission = SCOPES_NAMES?.some((scope) =>
    ability?.can("create", `Flow.main@${scope}`)
  );

  // En-tête de colonne avec filtre
  const renderHeader = (columnId, label) => (
    <TableHeaderCell className="agentai-list-cell-bold" {...headerSortProps(columnId)}>
      <div className="header-filter-container">
        <div className="div-filter-style">
          {label}
          <CiFilter
            className="filter-icon"
            onClick={(e) => {
              e.stopPropagation();
              toggleFilterVisibility(columnId);
            }}
          />
        </div>{" "}
        {filterVisible[columnId] && (
          <Input
            className="input-filter-style"
            value={filters[columnId]}
            onClick={(e) => e.stopPropagation()}
            onChange={(e, data) => handleFilterChange(columnId, data.value)}
            placeholder={t("FILTER")}
            size="small"
          />
        )}
      </div>
    </TableHeaderCell>
  );

  return (
    <>
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <h2>{t("AGENT_AI_LIST", { defaultValue: "AI Agents" })}</h2>
          <Button
            appearance="primary"
            onClick={addAgentAI}
            className="agentai-list-button-style"
            disabled={!hasAnyCreatePermission}
          >
            <Add20Filled /> {t("ADD_AGENT_AI", { defaultValue: "New AI Agent" })}
          </Button>
        </div>

        <div className="table-container">
          <div className="agentai-list-table-wrapper">
            <Table
              role="grid"
              aria-label="Agent AI table with filters and sorting"
              className="agentai-list-table-style"
              sortable
            >
              <TableHeader>
                <TableRow className="agentai-list-header-style">
                  {renderHeader("NAME", t("NAME", { defaultValue: "Name" }))}
                  {renderHeader("ENTITY", t("ENTITY", { defaultValue: "Entity" }))}
                  {renderHeader("VOICE", t("VOICE", { defaultValue: "Voice" }))}
                  <TableHeaderCell className="agentai-list-cell-bold">{t("ACTION")}</TableHeaderCell>
                </TableRow>
              </TableHeader>

              <TableBody>
                {rows.map(({ item }) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <TableCellLayout>{item._name}</TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>
                        <Badge appearance="tint">{item._entity}</Badge>
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>{item._voice}</TableCellLayout>
                    </TableCell>
                    <TableCell className="agentai-list-cell-right">
                      <Button
                        appearance="subtle"
                        icon={<EditRegular />}
                        aria-label="Edit"
                        onClick={() => selectAgentAI(item.id)}
                        disabled={!item.permissions?.main?.canUpdate}
                      />
                      <Button
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        aria-label="Delete"
                        onClick={() => setSelectedAgentAI(item)}
                        disabled={!item.permissions?.main?.canDelete}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {selectedAgentAI && (
          <ConfirmAlert
            open={!!selectedAgentAI}
            name={selectedAgentAI._name}
            onConfirm={() => removeAgentAI(selectedAgentAI.id, selectedAgentAI._entity)}
            onCancel={() => setSelectedAgentAI(null)}
          />
        )}
      </div>
    </>
  );
};

export default AgentAIListComponent;
